/**
 * Variant Collection Controller for LGN
 * Orchestrates filter changes, API calls, and DOM updates for variant-based display
 */

 const t = window.themeTranslations.search;

function extractFilterValue(filterInputItem) {
  if (filterInputItem.variantMetafield) return filterInputItem.variantMetafield.value;
  if (filterInputItem.productMetafield) return filterInputItem.productMetafield.value;
  if (filterInputItem.productVendor) return filterInputItem.productVendor;
  if (filterInputItem.productType) return filterInputItem.productType;
  if (filterInputItem.variantOption) return filterInputItem.variantOption.value;
  if (filterInputItem.available !== undefined) return String(filterInputItem.available);
  return null;
}

class VariantCollection {
  constructor(containerSelector = 'custom-collection-product-list') {
    this.container = document.querySelector(containerSelector);
    if (!this.container) {
      console.error('[VariantCollection] Container not found:', containerSelector);
      return;
    }

    // Get configuration from data attributes
    this.collectionHandle = this.container.dataset.collectionHandle;
    if (!this.collectionHandle) {
      console.error('[VariantCollection] No collection handle found');
      return;
    }

    // Initialize components
    this.api = new StorefrontAPI();
    this.cardRenderer = new VariantCard({
      imageRatio: this.container.dataset.imageRatio || 'square',
      showVendor: this.container.dataset.showVendor !== 'false',
      showPrice: this.container.dataset.showPrice !== 'false',
      showSize: this.container.dataset.showSize !== 'false',
      showCartBtn: this.container.dataset.showCartBtn !== 'false'
    });

    // DOM references
    this.productsGrid = this.container.querySelector('#collection-product-list__products');
    this.productsWrapper = this.container.querySelector('#collection-product-list__products-wrapper');
    this.productCount = this.container.querySelector('.collection-header__count');
    this.filtersForm = document.querySelector('#CollectionFiltersForm');
    this.loadMoreBtn = null;

    // State
    this.variants = [];
    this.displayedCount = 0;
    this.variantsPerPage = parseInt(this.container.dataset.itemsPerPage) || 15;
    this.currentFilters = {};
    this.currentCursor = null;
    this.hasMoreProducts = true;
    this.isLoading = false;

    // Initialize
    this.init();
  }

  async init() {
    // Parse initial filters from URL
    this.currentFilters = StorefrontAPI.parseURLFilters(new URLSearchParams(window.location.search));

    // Apply default filters from collection metafields (if no URL filters override them)
    const defaultContenance = this.container.dataset.defaultFilterContenance;
    const defaultConditionnement = this.container.dataset.defaultFilterConditionnement;
    if (defaultContenance && !this.currentFilters['filter.v.m.custom.contenance']) {
      this.currentFilters['filter.v.m.custom.contenance'] = defaultContenance.split(',').map(v => v.trim()).filter(Boolean);
    }
    if (defaultConditionnement && !this.currentFilters['filter.v.m.custom.conditionnement']) {
      this.currentFilters['filter.v.m.custom.conditionnement'] = defaultConditionnement.split(',').map(v => v.trim()).filter(Boolean);
    }
    // Update URL to reflect default filters
    if (defaultContenance || defaultConditionnement) {
      this.updateURL();
    }

    // Show loading state
    this.showLoading();

    // Bind filter events (initial bindings for Liquid-rendered filters)
    this.bindFilterEvents();

    // Fetch and render dynamic filters
    await this.fetchAndRenderFilters();

    // Initial fetch of variants
    await this.fetchVariants();

    // Render initial results
    this.renderVariants();
  }

  /**
   * Bind filter checkbox change events
   */
  bindFilterEvents() {
    if (!this.filtersForm) return;

    // Checkbox filters
    this.filtersForm.querySelectorAll('.filter-group__checkbox').forEach(checkbox => {
      checkbox.addEventListener('change', () => this.onFilterChange());
    });

    // "Voir plus" buttons
    this.filtersForm.querySelectorAll('.filter-group__voir-plus').forEach(button => {
      button.addEventListener('click', (e) => this.onVoirPlusClick(e));
    });

    // Main filters toggle
    const filtersWrapper = document.querySelector('.collection-filters-wrapper');
    const filtersHeader = filtersWrapper?.querySelector('.collection-filters__header');
    if (filtersHeader) {
      filtersHeader.addEventListener('click', () => {
        const isCollapsed = filtersWrapper.dataset.collapsed === 'true';
        filtersWrapper.dataset.collapsed = isCollapsed ? 'false' : 'true';
        filtersHeader.setAttribute('aria-expanded', isCollapsed ? 'true' : 'false');
      });
    }
  }

  /**
   * Handle "Voir plus" / "Voir moins" toggle
   */
  onVoirPlusClick(event) {
    const button = event.currentTarget;
    const group = button.closest('.filter-group');
    const items = group.querySelectorAll('.filter-group__item');
    const maxVisible = parseInt(button.dataset.maxVisible, 10) || 4;
    const isExpanded = button.dataset.expanded === 'true';

    items.forEach((item, index) => {
      const checkbox = item.querySelector('.filter-group__checkbox');
      if (checkbox?.checked) return; // Always show active items

      if (index >= maxVisible) {
        item.classList.toggle('filter-group__item--hidden', isExpanded);
      }
    });

    button.dataset.expanded = isExpanded ? 'false' : 'true';
    const textSpan = button.querySelector('.filter-group__voir-plus-text');
    if (textSpan) {
      textSpan.textContent = isExpanded
        ? (button.dataset.textMore || t.viewMore)
        : (button.dataset.textLess || t.viewLess);
    }
  }

  /**
   * Fetch all available filters and render complete filter UI
   */
  async fetchAndRenderFilters() {
    try {
      // Build current filter state for counting
      const appliedFilters = StorefrontAPI.buildFilters(this.currentFilters);

      // Fetch all filter values
      const filterData = await this.api.fetchCollectionFilters(this.collectionHandle, {
        appliedFilters
      });

      // Render filters to DOM
      this.renderFilterGroups(filterData.filters);

    } catch (error) {
      console.error('[VariantCollection] Filter fetch error:', error);
      // Fallback: Keep existing Liquid-rendered filters
    }
  }

  /**
   * Render filter groups to DOM
   * @param {Array} filters - Filter groups from Storefront API
   */
  renderFilterGroups(filters) {
    if (!this.filtersForm) return;

    // Map Shopify filter IDs to our custom param names
    const filterMap = {
      'filter.p.vendor': {
        title: t.domains,
        sortOrder: 1
      },
      'filter.p.m.custom.millesime': {
        title: t.vintages,
        sortOrder: 2,
        sortValues: (values) => values.sort((a, b) => parseInt(b.label) - parseInt(a.label)) // Descending
      },
      'filter.v.m.custom.contenance': {
        title: t.sizes,
        sortOrder: 3,
        sortValues: (values) => values.sort((a, b) => parseInt(a.label) - parseInt(b.label)) // Ascending
      },
      'filter.v.m.custom.conditionnement': {
        title: t.packaging,
        sortOrder: 4,
        sortValues: (values) => {
          // Filter out UNIT and sort CBO numerically (CBO1, CBO2, CBO3, CBO6, CBO12)
          return values
            .filter(v => v.label !== 'UNIT')
            .sort((a, b) => {
              const numA = parseInt(a.label.replace('CBO', ''));
              const numB = parseInt(b.label.replace('CBO', ''));
              return numA - numB;
            });
        }
      }
    };

    // Clear existing filters
    this.filtersForm.innerHTML = '';

    // Sort and render filter groups
    const sortedFilters = filters
      .filter(f => filterMap[f.id]) // Only render mapped filters
      .sort((a, b) => {
        const orderA = filterMap[a.id]?.sortOrder || 999;
        const orderB = filterMap[b.id]?.sortOrder || 999;
        return orderA - orderB;
      });

    sortedFilters.forEach(filter => {
      const config = filterMap[filter.id];

      // Sort values if needed
      const sortedValues = config.sortValues
        ? config.sortValues([...filter.values])
        : filter.values;

      // Render filter group
      const html = this.renderFilterGroup(filter, sortedValues, config);
      this.filtersForm.insertAdjacentHTML('beforeend', html);
    });

    // Re-bind events
    this.bindFilterEvents();
  }

  /**
   * Render a single filter group as HTML
   * @param {Object} filter - Filter object from API
   * @param {Array} values - Sorted filter values
   * @param {Object} config - Filter configuration
   * @returns {string} HTML string
   */
  renderFilterGroup(filter, values, config) {
    const maxVisible = 4;
    const hasActive = values.some(v => {
    try {
        const parsed = JSON.parse(v.input);
        const inputs = Array.isArray(parsed) ? parsed : [parsed];

        return inputs.some(f => {
            const val = extractFilterValue(f);
            return val && this.currentFilters[filter.id]?.includes(val);
        });

    } catch (e) {
        return false;
    }
});

    let html = `
      <div class="filter-group" data-filter-param="${filter.id}" data-expanded="${hasActive}">
        <div class="filter-group__header">
          <span class="filter-group__title">${config.title}</span>
        </div>
        <div class="filter-group__content" id="filter-${filter.id.replace(/\./g, '-')}">
          <ul class="filter-group__list" role="list">
    `;

    values.forEach((value, index) => {
      const isHidden = index >= maxVisible && !hasActive;
      const isDisabled = value.count === 0;
      let filterInput, isActive;

      try {
    const parsed = JSON.parse(value.input);

    // Shopify renvoie parfois un objet, parfois un tableau
    filterInput = Array.isArray(parsed) ? parsed : [parsed];

    isActive = filterInput.some(f => {
        const val = extractFilterValue(f);
        return val && this.currentFilters[filter.id]?.includes(val);
    });

} catch (e) {
    console.error(e);
    filterInput = [];
    isActive = false;
}

      // Get display label (with translation if needed)
      const displayLabel = this.getFilterValueLabel(filter.id, value.label);

      html += `
        <li class="filter-group__item${isHidden ? ' filter-group__item--hidden' : ''}"
            data-index="${index + 1}"
            data-count="${value.count}">
          <label class="filter-group__label"
                 for="filter-${filter.id.replace(/\./g, '-')}-${index + 1}"
                 data-checked="${isActive}">
            <input type="checkbox"
                   class="filter-group__checkbox"
                   id="filter-${filter.id.replace(/\./g, '-')}-${index + 1}"
                   name="${filter.id}"
                   value="${(filterInput.length && extractFilterValue(filterInput[0])) || value.label}"
                   data-input='${value.input}'
                   ${isActive ? 'checked' : ''}>
            <span class="filter-group__checkbox-visual"></span>
            <span class="filter-group__text">
              ${displayLabel}
            </span>
          </label>
        </li>
      `;
    });

    html += `
      </ul>
      ${values.length > maxVisible && !hasActive ? `
      <button type="button"
              class="filter-group__voir-plus"
              data-max-visible="${maxVisible}"
              data-expanded="${hasActive}"
              data-text-more="${t.viewMore}"
              data-text-less="${t.viewLess}">
        <span class="filter-group__voir-plus-text">
          ${hasActive ? t.viewLess : t.viewMore}
        </span>
      </button>
      ` : ''}
    </div>
  </div>
`;

    return html;
  }

  /**
   * Get translated label for filter value
   * @param {string} filterId - Filter ID
   * @param {string} value - Raw value
   * @returns {string} Translated label
   */
  getFilterValueLabel(filterId, value) {
    // Map specific values to translations
    const labelMap = {
  'filter.v.m.custom.contenance': {
    '75': t.contenance['75'],
    '150': t.contenance['150'],
    '300': t.contenance['300'],
    '600': t.contenance['600'],
    '900': t.contenance['900'],
    '1500': t.contenance['1500'],
    '3000': t.contenance['3000']
  },
  'filter.v.m.custom.conditionnement': {
    'CBO1': t.conditionnement.CBO1,
    'CBO2': t.conditionnement.CBO2,
    'CBO3': t.conditionnement.CBO3,
    'CBO6': t.conditionnement.CBO6,
    'CBO12': t.conditionnement.CBO12
  }
};

    return labelMap[filterId]?.[value] || value;
  }

  /**
   * Handle filter change
   */
  async onFilterChange() {
    // Build filter state from UI
    this.currentFilters = this.buildFilterStateFromUI();

    // Update URL
    this.updateURL();

    // Reset pagination
    this.variants = [];
    this.displayedCount = 0;
    this.currentCursor = null;
    this.hasMoreProducts = true;

    // Show loading
    this.showLoading();

    // Fetch updated filters with new counts
    await this.fetchAndRenderFilters();

    // Fetch filtered variants
    await this.fetchVariants();

    // Render
    this.renderVariants();
  }

  /**
   * Build filter state object from checkbox UI
   */
  buildFilterStateFromUI() {
    const filterState = {};

    if (!this.filtersForm) return filterState;

    this.filtersForm.querySelectorAll('.filter-group__checkbox:checked').forEach(checkbox => {
      const paramName = checkbox.name;
      const value = checkbox.value;

      if (!filterState[paramName]) {
        filterState[paramName] = [];
      }
      filterState[paramName].push(value);
    });

    return filterState;
  }

  /**
   * Update URL with current filter state
   */
  updateURL() {
    const params = StorefrontAPI.buildURLParams(this.currentFilters);
    const newUrl = params ? `${window.location.pathname}?${params}` : window.location.pathname;
    window.history.replaceState({}, '', newUrl);
  }

  /**
   * Fetch variants from Storefront API
   */
  async fetchVariants() {
    if (this.isLoading) return;
    this.isLoading = true;

    try {
      const filters = StorefrontAPI.buildFilters(this.currentFilters);

      const result = await this.api.fetchCollectionVariants(this.collectionHandle, {
        filters,
        first: 50, // Fetch 50 products at a time
        after: this.currentCursor
      });

      // Append new variants
      this.variants = this.variants.concat(result.variants);
      this.currentCursor = result.pageInfo.endCursor;
      this.hasMoreProducts = result.pageInfo.hasNextPage;

      // Update product count
      this.updateProductCount();

    } catch (error) {
      console.error('[VariantCollection] Fetch error:', error);
      this.showError();
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Render variants to the grid
   */
  renderVariants() {
    if (!this.productsGrid) return;

    // Determine how many to show
    const toShow = this.variants.slice(0, this.displayedCount + this.variantsPerPage);
    this.displayedCount = toShow.length;

    if (toShow.length === 0) {
      this.productsGrid.innerHTML = VariantCard.renderEmpty();
      this.productsWrapper.dataset.empty = 'true';
      this.hideLoadMore();
      return;
    }

    this.productsWrapper.dataset.empty = 'false';

    // Render cards
    const html = toShow.map(variant => {
      return `
        <div class="col-lg-4 col-12 my-3 product-card__wrapper">
          ${this.cardRenderer.render(variant)}
        </div>
      `;
    }).join('');

    this.productsGrid.innerHTML = html;

    // Show/hide load more button
    if (this.displayedCount < this.variants.length || this.hasMoreProducts) {
      this.showLoadMore();
    } else {
      this.hideLoadMore();
    }
  }

  /**
   * Load more variants
   */
  async loadMore() {
    // First, show more from already-fetched variants
    if (this.displayedCount < this.variants.length) {
      this.renderVariants();
      return;
    }

    // If we need more, fetch from API
    if (this.hasMoreProducts) {
      this.showLoading(true); // Append loading
      await this.fetchVariants();
      this.renderVariants();
    }
  }

  /**
   * Show loading state
   */
  showLoading(append = false) {
    if (!this.productsGrid) return;

    const skeletons = VariantCard.renderSkeletons(this.variantsPerPage);
    const html = Array(Math.min(9, this.variantsPerPage)).fill(null).map(() => `
      <div class="col-lg-4 col-12 my-3 product-card__wrapper">
        ${VariantCard.renderSkeleton()}
      </div>
    `).join('');

    if (append) {
      this.productsGrid.insertAdjacentHTML('beforeend', html);
    } else {
      this.productsGrid.innerHTML = html;
    }
  }

  /**
   * Show error state
   */
  showError() {
    if (!this.productsGrid) return;

    this.productsGrid.innerHTML = `
      <div class="collection-product-list__products--empty py-5 my-5 text-center col-12">
        <h3>Une erreur est survenue</h3>
        <p>Veuillez rafraîchir la page ou réessayer plus tard.</p>
      </div>
    `;
  }

  /**
   * Update product count display
   */
  updateProductCount() {
    if (!this.productCount) return;

    const count = this.variants.length;
    const text = count === 1 ? '1 produit' : `${count} produits`;
    this.productCount.textContent = text;
  }

  /**
   * Show load more button
   */
  showLoadMore() {
    // If already tracking a button, just show it
    if (this.loadMoreBtn) {
      this.loadMoreBtn.style.display = 'inline-block';
      this.loadMoreBtn.closest('pagination-button').style.display = 'block';
      return;
    }

    // Find pagination button (variant mode vs legacy mode)
    const variantModePagination = this.productsWrapper.querySelector('pagination-button[data-variant-mode="true"]');
    const legacyPagination = this.productsWrapper.querySelector('pagination-button:not([data-variant-mode])');

    const paginationContainer = variantModePagination || legacyPagination;

    if (paginationContainer) {
      this.loadMoreBtn = paginationContainer.querySelector('.btn-load-more');

      if (this.loadMoreBtn) {
        // Show both button and container
        this.loadMoreBtn.style.display = 'inline-block';
        paginationContainer.style.display = 'block';

        // In variant mode, override click handler to use JS pagination
        if (variantModePagination) {
          // Remove existing click handler from pagination-button.js
          const newBtn = this.loadMoreBtn.cloneNode(true);
          this.loadMoreBtn.parentNode.replaceChild(newBtn, this.loadMoreBtn);
          this.loadMoreBtn = newBtn;

          // Bind our loadMore handler
          this.loadMoreBtn.addEventListener('click', (e) => {
            e.preventDefault();
            this.loadMore();
          });
        }
      }
    }
  }

  /**
   * Hide load more button
   */
  hideLoadMore() {
    if (this.loadMoreBtn) {
      this.loadMoreBtn.style.display = 'none';
      this.loadMoreBtn.closest('pagination-button').style.display = 'none';
    }
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  // Only initialize if container has variant-mode enabled
  const container = document.querySelector('custom-collection-product-list[data-variant-mode="true"]');
  if (container) {
    window.variantCollection = new VariantCollection();
  }
});

// Re-initialize after Shopify section reload (for theme editor)
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('custom-collection-product-list[data-variant-mode="true"]')) {
    window.variantCollection = new VariantCollection();
  }
});

// Export for use in other modules
window.VariantCollection = VariantCollection;
